<script setup>
import { computed, ref, watch, nextTick, onMounted } from 'vue';
import IconClose from './../components/icons/IconClose.vue';
import IconError from './../components/icons/IconError.vue';
import IconPasswordHide from './../components/icons/IconPasswordHide.vue';
import IconPasswordShow from './../components/icons/IconPasswordShow.vue';
import IconSearch from './../components/icons/IconSearch.vue';
import InfoTooltip from './pge-info-tooltip.vue';

const props = defineProps({
  label: String,
  labelType: {
    type: String,
    default: 'floated',
    validator: v => ['floated', 'separated'].includes(v)
  },
  model: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    default: 'text',
    validator: (value) => {
      return ['text', 'email', 'password', 'search', 'url'].includes(value);
    },
  },
  name: String,
  placeholder: String,
  minLength: {
    type: Number,
    default: 1,
  },
  maxLength: Number,
  errorMessage: {
    type: String,
    default: 'Cannot be blank',
  },
  tooltip: {
    type: String,
    default: ''
  },
  backgroundColor: String,
  info: String,
  compact: Boolean,
  borderCircle: Boolean,
  hasSearchIcon: Boolean,
  hasTrailingIcon: Boolean,
  hasTrailingButton: Boolean,
  disabled: Boolean,
  required: Boolean,
  focused: Boolean,
  hasRequiredSign: Boolean,
  shouldShowError: Boolean,
  hasError: Boolean,
  noTabindex: {
    type: Boolean,
    default: false
  },
  submitOnEnter: {
    type: Function,
    default: () => {},
  }
});

const emit = defineEmits(['input', 'change', 'update:focused', 'update:hovered']);
const inputEl = ref(null);
const localModel = ref(props.model || '');
const isFocused = ref(props.focused);
const isHovered = ref(false);
const inputType = ref(props.type);

// `shouldShowError` is driven from outside (a server said no), so it is not
// gated on touch; only the "you have not filled this in" half is.
const showRequiredError = computed(() => props.required
  && isFocused.value
  && (props.shouldShowError || (touched.value && (localModel.value?.length ?? 0) < props.minLength)));

/**
 * Whether the viewer has actually engaged with this field yet.
 *
 * A required field shown empty-and-red before a single keystroke is not a
 * validation message, it is an accusation — and it is what the Overview's
 * "Make something" tiles produced, since they land the cursor in an empty
 * required field on purpose. The error waits for a keystroke or a blur.
 */
const touched = ref(false);

const handleInput = (e) => {
  touched.value = true;
  emit('input');
  emit('update:modelValue', e.target.value);
};

const handleFocus = () => {
  if (props.disabled) return;
  isFocused.value = true;
  emit('update:focused', true);
};
const handleHover = (state) => {
  if (props.disabled) return;
  isHovered.value = state;
  emit('update:hovered', state);
};
const handleBlur = () => {
  touched.value = true;
  isFocused.value = false;
  emit('update:focused', false);
};

const handleChange = (e) => {
  emit('change', e);
};

const handleTrailingIconClick = () => {
  if(props.type === 'password') {
    inputType.value = inputType.value === 'text' ? 'password' : 'text';
  } else {
    localModel.value = '';
    emit('update:modelValue', '');
  }
};

watch(() => props.focused, async (newValue) => {
  isFocused.value = newValue;
  if (isFocused.value) {
    await nextTick();
    inputEl.value.focus();
  }
});

watch(() => props.model, (newValue) => {
  localModel.value = newValue;
});

onMounted(async () => {
  if (props.focused) {
    await nextTick();
    inputEl.value.focus();
  }
});
</script>

<template>
  <div class="d-container"
    :style="{
      rowGap: !tooltip ? '8px' : '2px',
      minHeight: compact ? '64px' : '80px'
    }"
  >
    <label
      :tabindex="noTabindex ? '' : 0"
      :class="disabled ? 'disabled' : undefined"
      @focus="handleFocus"
      @mouseover="handleHover(true)"
      @mouseleave="handleHover(false)"
    >
      <span
        v-if="label"
        :class="[
          labelType,
          isFocused ? 'focused' : undefined,
          isHovered ? 'hovered' : undefined,
          showRequiredError ? 'error' : undefined
        ]"
        :style="{ backgroundColor: backgroundColor ? backgroundColor : 'inherit' }"
      >
        {{ label }}
        <span v-if="required && hasRequiredSign" class="required-sign">*</span>
      </span>
      <div
        :tabindex="noTabindex ? '' : 0"
        class="input-container"
        :class="[
          showRequiredError ? 'error' : undefined,
          isFocused ? 'focused' : undefined,
          isHovered ? 'hovered' : undefined,
          disabled ? 'disabled' : undefined,
          compact ? 'compact' : undefined,
          borderCircle ? 'border-circled' : undefined,
        ]"
        @focus="handleFocus"
      >
        <span v-if="hasSearchIcon" class="pge-icon">
          <icon-search />
        </span>
  
        <input
          ref="inputEl"
          :type="inputType"
          v-model="localModel"
          :name="name"
          :placeholder="placeholder"
          :disabled="disabled"
          :maxlength="maxLength"
          :required="required"
          :class="[showRequiredError ? 'error' : undefined]"
          @keyup.enter="() => { props.submitOnEnter() }"
          @input="handleInput"
          @focus="handleFocus"
          @blur="handleBlur"
          @change="handleChange"
        />
  
        <span
          v-if="hasTrailingIcon && localModel?.length"
          role="button"
          aria-labelledby="Clear Input"
          class="pge-icon clear"
          @click="handleTrailingIconClick"
        >
          <template v-if="type === 'password'">
            <icon-password-show v-if="inputType === 'password'" />
            <icon-password-hide v-else />
          </template>
          <icon-close color="#49454F" v-else />
        </span>
        <span
          v-else-if="hasTrailingIcon && showRequiredError"
          class="pge-icon"
        >
          <icon-error />
        </span>

        <div v-if="hasTrailingButton">
          <slot name="trailing-button" />
        </div>
      </div>
    </label>

    <div
      v-if="tooltip || maxLength || info || showRequiredError || hasError"
      class="d-container__description"
    >
      <div class="d-container__description__info">
        <div class="d-container__description__info__inner">
          <span
            v-if="showRequiredError || hasError"
            class="error"
          >
            {{ errorMessage || '' }}
          </span>
          <span
            v-if="maxLength && isFocused"
            :class="showRequiredError ? 'error' : undefined"
          >
            {{ localModel?.length }} / {{ maxLength }} Characters Maximum
          </span>
    
          <span v-else-if="maxLength && !isFocused">{{ maxLength }} Characters Maximum</span>
          <span v-else-if="info">{{ info }}</span>
        </div>

        <info-tooltip v-if="tooltip" :tooltip="tooltip" />
      </div>
    </div>
  </div>
</template>

<style scoped>
@import url("./styles/pge-input-text/index.scss");
</style>
